import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { logger } from '../utils/logger';

const ModulePermissionsContext = createContext();

export const useModulePermissions = () => {
  const context = useContext(ModulePermissionsContext);
  if (!context) {
    throw new Error('useModulePermissions must be used within ModulePermissionsProvider');
  }
  return context;
};

export const ModulePermissionsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // ✅ CORRECTION: Ne charge les permissions que si l'utilisateur est connecté
    if (!currentUser) {
      setPermissions([]);
      setLoading(false);
      setError(null);
      return;
    }

    const loadPermissions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Charger les permissions de l'utilisateur
        const userRole = currentUser.role || 'member';
        
        // Charger depuis Firestore
        const permissionsRef = collection(db, 'modulePermissions');
        const q = query(permissionsRef, where('role', '==', userRole));
        const querySnapshot = await getDocs(q);
        
        const userPermissions = [];
        querySnapshot.forEach((doc) => {
          userPermissions.push({ id: doc.id, ...doc.data() });
        });

        setPermissions(userPermissions);
        logger.log('Permissions chargées:', userPermissions);
      } catch (error) {
        console.error('❌ Erreur chargement permissions:', error.message);
        logger.error('Erreur chargement permissions:', error);
        setError(error);
        // En cas d'erreur, on continue avec des permissions vides
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [currentUser]); // ⚠️ Important: se déclenche quand currentUser change

  const hasPermission = (module, action = 'view') => {
    if (!currentUser) return false;
    
    // Admin a toutes les permissions
    if (currentUser.role === 'admin') return true;
    
    // Vérifier les permissions spécifiques
    return permissions.some(
      perm => perm.module === module && perm.permissions?.includes(action)
    );
  };

  const value = {
    permissions,
    loading,
    error,
    hasPermission
  };

  return (
    <ModulePermissionsContext.Provider value={value}>
      {children}
    </ModulePermissionsContext.Provider>
  );
};
